/** @module csi/def/Relationship */
define([
	"dojo/_base/array",
	"dojo/_base/declare",
	"dojo/_base/lang",
	"csi",
	"csi/_base/underscore",
	"csi/access/crews",
	"csi/config/preferences",
	"csi/core/ExtendedPath",
	"csi/def",
	"csi/def/ActionFactory",
	"csi/def/Actions",
	"csi/def/AddPath",
	"csi/def/PreferencePath",
	"csi/def/Rule",
	"csi/reflection",
	"csi/reflectionHints",
	"csi/resolver",
	"csi/service/def/relationshipService",
	"csi/util",
	"csi/xml",
	"csi/xml/Query",
	"csi/xml/QueryProcessor",
	"csi/xml/Search",
	"src/c8-platform/service/dataStoreService",
], function(array, declare, lang, csi, _, crews, configPreferences, ExtendedPath, def, ActionFactory, Actions, AddPath, PreferencePath, Rule, reflection, reflectionHints, resolver,
	{parseSort, processSorts}, util, xml, Query, QueryProcessor, Search, {getDataStoreComponents}) {

	// Object: search options for relationship
	var SEARCH_OPTIONS = {
		module: null,
		operation: null,
		bMask: false,
		dividers: true,
		filter: true,					// general view filters
		filterOmit: null,				// omit specific heading from filtering
		filterType: true,				// add type filter to optimize queries that do not have it
		paths: true,
		sort: true,
		url: null,
		processExpressions: true
	};

	return declare(null, {
		RowActions: {
			AggregateFrom: true,
			EditAll: true,
			New: true,
			NewFrom: true,
			MoveFrom: true
		},

		// Object: other request parameters for search
		m_parameters: null,

		// if query is complex, specify target class explicitly
		m_target: null,

		// "0/1" indicates if this relationship is recursive (tasks referencing subtasks...)
		m_bRepeat: null,

		m_url: null,
		m_id: null,
		m_module: null,
		moveAction: null,
		m_operation: null,
		m_typeNode: null,
		m_filters: null,
		m_depPaths: null,
		m_addPaths: null,
		m_prefPaths: null,
		m_sorts: null,
		m_rules: null,

		// actions on relationship and results
		m_news: null,
		m_defActions: null,

		// runtime
		m_query: null,
		m_rcSource: null,
		m_rcTarget: false,

		// Boolean: indicates if relationship is ordered - specified explicitly or derived from model
		//bOrdered: false,

		// Boolean: indicates if result nodes of query are read only - set explicitly to false to override default behavior
		// based on Relationship Id or AddPath
		//bReadOnly: false,

		// Boolean: indicates if result nodes are along a "simple" path, meaning there is a single path and all steps but last are multiplicity one (ref attributes)
		//bSimplePath: false,

		// String: indicates type of divider that can be stored in the relationship
		//dividerType: null,

		// IDataStoreSort[]: array of sorts for more complicated ordered relationships
		indexSorts: null,

		// Number: key used to identify relationship in view
		key: null,

		// String: indicates the path hints in a view
		pathHints: null,

		/** @property {PathHints} [pathHintsContext] - identifier for contextual path hints for results of Relationship query */
		//pathHintsContext: null,

		/** @property {ExtendedPath} [targetPath] - path to target class from given root class */
		//targetPath: null,

		// String: attribute storing sort order for relationship target type
		//typeSortAttribute: null,

		/** @property {DataStoreId} - DataStoreId defining the server query and meta-data about the nature of the model for the resulting data */
		dataStoreId: null,

		constructor: function(/*XMLNode*/xn) {
			if (!xn) return;

			var gp = lang.hitch(util, util.getProperty);
			var gd = lang.hitch(util, util.getDef);
			var gs = lang.hitch(util, util.getDefs);

			this.m_url = gp(xn, "URL");
			this.m_id = gp(xn, "Id");
			this.m_typeNode = gp(xn, "Type");
			this.m_module = gp(xn, "Module");
			this.m_operation = gp(xn, "Operation");
			this.m_target = gp(xn, "Target");
			this.dataStore = gp(xn, "DataStore");
			this.m_bRepeat = util.getBooleanProperty(xn, "Repeat");

			this.m_filters = util.getElements(xn, "Filter/child::*");
			this.m_depPaths = util.getProperties(xn, "DependencyPath", "Path");
			this.m_addPaths = gs(xn, "AddPath", AddPath);
			this.m_prefPaths = gs(xn, "PreferencePath", PreferencePath);
			this.m_sorts = gs(xn, "Sort", parseSort, true);
			this.m_rules = gs(xn, "Rule", Rule);

			// set target and id of relationship if data store is provided
			if (this.dataStore) {
				const {target, relation} = getDataStoreComponents(this.dataStore);
				this.m_target = target;
				this.m_id = relation;
			}

			// resolve path hints
			this.pathHints = gp(xn, "PathHints");
			if (this.pathHints) {
				this.pathHintsContext = configPreferences.getContextualPathHints(this.pathHints);

				if (!this.pathHintsContext) {
					console.warn("PathHints not found: " + this.pathHints);
				}
			}

			var actions = this.RowActions;
			var news = [];

			array.forEach(util.sn(xn, "child::*"), function(ch) {
				var tag = ch.getAttribute("Tag") || ch.tagName;

				if (actions[tag]) {
					news.push(ch);
				}
			}, this);

			if (news.length) {
				this.m_news = gs(news, null, ActionFactory.newAction, true);
			}

			this.m_defActions = gd(xn, "Actions", Actions);

			// determined from model if not specified and if possible (could be ambiguous)
			var defOrdered = util.ssn(xn, "Ordered");
			if (defOrdered) {
				this.bOrdered = true;
				this.indexSorts = gs(defOrdered, "Sort", parseSort, true);

				// DE10487 - would like a better solution, but due to query issues we can only apply this logic in specific places
				this.dividerType = util.getProperty(defOrdered, "DividerType");
			}

			// load extra request parameters
			var params = gp(xn, "Parameters");
			if (params) {
				this.m_parameters = csi.fromJson(params);
			}
		},

		copyQuery: function(/*Relationship*/r) {
			// summary: copy query configuration
			this.m_filters = r.m_filters;
			this.m_module = r.m_module;
			this.m_operation = r.m_operation;
			this.m_typeNode = r.m_typeNode;
			this.m_url = r.m_url;

			if (r.m_addPaths) {
				this.m_addPaths = array.map(r.m_addPaths, function(ap) {
					return lang.mixin(new AddPath(), {m_path: ap.m_path});
				});
			}
		},

		resetActions: function() {
			this.m_news = null;
			this.m_defActions = null;
		},

		resolve: function(lmap, rcRoot, nodeRoot, cache, bForQuery) {
			// summary: determine source and target classes for relationship and resolve paths and actions

			// switch context if absolute URL is specified
			if (this.m_url) {
				nodeRoot = resolver.resolveNode(this.m_url, true, cache);
				rcRoot = reflection.getNodeClass(nodeRoot);
			}

			// resolve paths in special cases where using parent context
			this.m_addPaths?.forEach((ap) => ap.resolvePath(rcRoot, nodeRoot));

			// store source and target classes of Relationship
			if (!this.getTargetClass(rcRoot)) {
				// this can happen when working with reflection model class and attribute views and is ok
				if (reflection.isInitialized()) {
					console.warn("csi/def/Relationship: target class not resolved.", this);
				}

				this.m_rcTarget = null;
			}

			// if not ordered relationship, determine if results are sorted by value in designated attribute
			if (!this.bOrdered) {
				this.typeSortAttribute = reflectionHints.getSortAttribute(this.getTargetType());
			}

			// skip actions if just resolving for query
			if (!bForQuery) {
				if (this.m_news) {
					array.forEach(this.m_news, function(a) {
						a.m_relationship = this;

						// New actions will either open dialog or finish and start editing, so there is no reason to disable other actions in view 
						a.bKeepViewEnabled = true;
					}, this);

					def.resolveDefs(this.m_news, lmap, this.m_rcSource, nodeRoot);
				}

				if (this.m_defActions) {
					array.forEach(this.m_defActions.m_actions, function(a) {
						a.m_relationship = this;

						// track if there is a moveAction - doesn't matter which one
						if (a.m_tag == "MoveDown" || a.m_tag == "MoveUp") {
							this.moveAction = a;
						}
					}, this);

					this.m_defActions.resolve(lmap, this.m_rcTarget, null);
				}
			}

			return this.m_rcTarget;
		},

		getOnlyPath: function() {
			// summary: get relationship path if there is one (and only one)
			if (this.m_id && !this.m_addPaths) {
				return xml.pathCreate(xml.PATH_OP_CHILD, this.m_id);
			} else if (!this.m_id && this.m_addPaths && (this.m_addPaths.length == 1)) {
				return this.m_addPaths[0].m_path;
			}
			return null;
		},

		getTargetClass: function(rcRoot) {
			// summary: stores and returns the target reflection class of this relationship
			const {m_addPaths, m_id, m_rcTarget, m_target, m_typeNode} = this;
			let rc = m_rcTarget;
			if (rc) {
				return rc;
			}

			const addPath = (m_addPaths?.length === 1) ? m_addPaths[0] : null;
			const onlyPath = this.getOnlyPath();
			let path = m_id ? xml.path(m_id) : onlyPath;

			// handle special case with AddPath with To property when resolving target class without knowledge of node instance type (just model);
			// in this case, the read-only and ordered nature of the view are not as important as getting the target class right.
			if (rcRoot?.abstract && addPath?.m_typeToFind) {
				rcRoot = reflection.getClass(addPath.m_typeToFind);
				path = addPath.m_pathSuffix || "";
			}

			// try to resolve class using limit path for query, if any; at the same time, track whether or not path is editable or ordered
			if (rcRoot && (path != null)) {
				var bOrdered = true;
				var bSimple = true;
				var bSingle = null;
				var bReadOnly;

				if (path != onlyPath) {
					bSimple = this.bSimplePath = false;
				}

				// TODO: Add functions to ExtendedPath to extract this information from the path

				function onStep(rcNext, rcPrevious, idRelation, bChild) {
					var rcr = rcPrevious.getRelation(idRelation, true, true);

					if (!bReadOnly) {
						bReadOnly = !bChild || !rcPrevious.isRelationEditable(idRelation);
					}

					if (!bChild) {
						bOrdered = false;
					} else if (bOrdered) {
						bOrdered = rcr && (rcr.isSingle || rcr.isOrdered());
					}

					// track "simple" status of path - want all but last step to be single
					if (bSimple && (bSingle !== null)) {
						bSimple = bSingle;
					}

					if (bSimple) {
						bSingle = rcr ? rcr.isSingle : false;
					}
				}

				rc = rcRoot.getPathClass(path, false, null, onStep);

				if (this.bReadOnly === undefined) {
					this.bReadOnly = bReadOnly;
				}

				if (this.bOrdered === undefined) {
					this.bOrdered = bOrdered;
				}

				if (this.bSimplePath === undefined) {
					this.bSimplePath = bSimple;
				}
			}

			// if target class specified explicitly, then set it now - this could override result above because it
			// could be more explicit (derived class vs base class)
			if (m_target || m_typeNode) {
				rc = reflection.getClass(m_target || m_typeNode);
			}

			this.m_rcSource = rcRoot;
			this.m_rcTarget = rc;

			if (rc && rcRoot && (path != null)) {
				this.targetPath = ExtendedPath.create(rcRoot, path, this.pathHintsContext);
				this.targetPath?.setEndType(rc.type);
			}

			return rc;
		},

		getTargetType: function() {
			// summary: return target type of relationship - getTargetClass can be null for debug views of reflection model
			if (this.m_typeNode) return this.m_typeNode;
			var rc = this.getTargetClass();
			return rc ? rc.getType() : null;
		},

		hasFilterExpression: function() {
			// summary: determine if any of the relationship filters use expression - test by looking for "("
			if (this.m_filters) {
				return array.some(this.m_filters, xml.filterHasExpression, xml);
			}

			return false;
		},

		hasNewAction: function() {
			if (this.m_news) {
				return true;
			}

			var actions = this.m_defActions ? this.m_defActions.getActions() : null;
			if (actions) {
				return array.some(actions, function(a) {return a.m_tag == "Insert";});
			}

			return false;
		},

		getAttributeId: function() {
			return this.m_id;
		},

		getActions: function() {
			var a = [];

			if (this.m_defActions && this.m_defActions.m_actions) {
				a = a.concat(this.m_defActions.m_actions);
			}

			if (this.m_news) {
				a = a.concat(this.m_news);
			}

			return a;
		},

		getAction: function(idAction) {
			var action = this.m_defActions ? this.m_defActions.getAction(idAction) : null;
			return action || csi.search(this.m_news, function(a) {return a.m_id == idAction;});
		},

		isRelative: function() {
			// summary: return true if query is global - no id or limit paths, unless from a specific url
			return !this.m_url && (this.m_id || this.m_addPaths);
		},

		addQuery: function(/*Query*/q, /*Boolean*/bReset) {
			if (bReset || !this.m_query) {
				this.m_query = q;
			} else {
				this.m_query.importQuery(q);
			}
		},

		addFilters: function(/*XML[]*/filters, /*Cache*/cache, /*Node*/node, /*Boolean*/bReset) {
			if (filters) {
				var q = new Query();
				q.importElements(filters);

				if (!q.isEmpty()) {
					QueryProcessor.evaluateQueryExpressions(q, node, cache);

					if (bReset || !this.m_filters) {
						this.m_filters = [];
					}

					var children = q.element.childNodes;
					for (var i = 0; i < children.length; ++i) {
						this.m_filters.push(children[i].cloneNode(true));
					}
				}
			}
		},

		/**
		 * Get the filter query
		 * @return {Query | undefined} 
		 */
		_getFilterQuery: function() {
			const {m_filters, m_query} = this;
			let query;

			if (m_query) {
				query ||= new Query();
				query.importQuery(this.m_query);
			}

			if (m_filters) {
				query ||= new Query();
				query.importElements(this.m_filters);
			}

			return query;
		},

		getConfiguredSearch: function(/*Cache*/cache, /*String*/urlNode, /*Object?*/options) {
			// summary: configure search with given options
			var urlRoot = this.m_url || urlNode;

			// initialize options - if passed options are mask, then apply against default options
			if (!options) {
				options = lang.delegate(SEARCH_OPTIONS);
			} else if (options.bMask) {
				_.extend(options, _.omit(SEARCH_OPTIONS, _.keys(options)));
			}

			options.url = urlRoot;

			if (options.operation || this.m_operation) {
				options.customOperation = true;
			}

			// initialize search
			var obj = {Module: options.module || this.m_module, Operation: options.operation || this.m_operation};
			if (xml.isValidURL(urlNode)) {
				obj[xml.PROP_URL] = urlNode;
			}

			if (!crews.isAdmin()) {
				obj["Crew.Scope"] = "Result";
				obj["Fmt.Crew"] = "Name";
			}

			var search = new Search(this.m_parameters ? lang.mixin(obj, this.m_parameters) : obj);
			var query = search.getQuery();
			var rc = this.m_rcTarget;

			// add limit attribute/path(s)
			if (xml.isValidURL(urlRoot)) {
				if (this.m_addPaths) {
					for (var i = 0; i < this.m_addPaths.length; ++i) {
						search.setLimitPath(urlRoot, this.m_addPaths[i].m_path);
					}
				}

				if (this.m_id) {
					search.setLimitAttribute(urlRoot, this.m_id);
				}
			}

			// configure query filters and process any expressions in filter values
			if (options.filter) {
				const relQuery = this._getFilterQuery();

				if (this.m_typeNode) {
					query.Node(xml.PROP_TYPE, xml.OP_EQUALS, this.m_typeNode);
				}

				if (relQuery) {
					query.importQuery(relQuery);

					if (options.processExpressions) {
						// process expressions on query filters based on node context
						QueryProcessor.evaluateQueryExpressions(query, resolver.resolveNode(urlRoot, true, cache), cache);
					}
				}
			}

			if (options.sort) {
				var sq = search.getSortQuery(true);

				if (this.bOrdered) {
					// apply any sorts for ordered relationship
					array.forEach(this.indexSorts, ({descending, id, path}) => {
						sq.SortByIndex(id, path, descending);
					});
				} else if (this.m_sorts) {
					// apply pre-defined sorts
					processSorts(this.m_sorts, sq);
				} else if (this.typeSortAttribute) {
					// sort by default attribute per target type
					sq.SortByAttr(this.typeSortAttribute);
				} else {
					// sort by node name if no specific sort is specified
					sq.SortByNode(xml.PROP_NAME);
				}
			}

			if (rc && !rc.abstract && !rc.hasDerivedClasses()) {
				if (query.isEmpty()) {
					// if doing ValuesByXML query from filter pane for attributes on second relationship or higher, then
					// we don't know the URL context, so we just query by type
					if (!xml.isValidURL(urlRoot)) {
						query.Type(rc.getType());
					}
				} else if (options.filter) {
					// if type not defined explicitly for limit path, then add type filter based on target class if not abstract
					if (options.filterType && !this.m_typeNode) {
						query.Type(rc.getType());
					}

					// handle special case of dividers where we always want to show dividers ragardless of filter
					// along with other nodes of type that match filters
					if (options.dividers && this.dividerType) {
						var qt = search.getQuery(true).Or();
						qt.Node(xml.PROP_TYPE, xml.OP_EQUALS, this.dividerType);
						qt.And().importQuery(query);
					}
				}
			}

			return search;
		},

		/**
		 * Set the type and target class of the Relationship
		 * @param {string} type
		 */
		setType: function(type) {
			this.m_typeNode = type;
			this.m_rcTarget = reflection.getClass(type);
		}
	});
});